import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box } from '@chakra-ui/react';

import ConfirmActionWarningModal from '../../../../components/ConfirmActionWarningModal';
import StackedButtonGroup from '../../../../components/Modal/ModalButtonGroups/StackedButtonGroup';
import LoadingSpinner from '../../../../components/Spinner';
import { createToast } from '../../../../redux/modules/app';
import {
  deleteCompanyCustomDomain,
  getCompanyCustomDomainDetails,
  selectCompanyCustomDomain,
  selectCompanyCustomDomainCreatingLoading,
  selectCompanyCustomDomainDeleteLoading,
  selectCompanyCustomDomainDetailsLoading,
  selectCompanyCustomDomainVerifyLoading,
  selectCompanyFeatures,
  selectCompanySettings, updateCompanySetting,
  verifyCompanyCustomDomain
} from '../../../../redux/modules/company';
import { hideModal, showModal } from '../../../../redux/modules/modal';

import CustomDomainPayWall from './components/CustomDomainPayWall';
import DomainRecords from './components/DomainRecords';
import RegisterDomainModal from './components/RegisterDomainModal';
import RegisterNewDomainHeader from './components/RegisterNewDomainHeader';
import VerifyDomainHeader from './components/VerifyDomainHeader';

const CustomDomainPanel = ({ props }) => {
  const dispatch = useDispatch();
  const { companyId } = useParams();
  const companySettings = useSelector(selectCompanySettings);
  const companyFeatures = useSelector(selectCompanyFeatures);
  const companyDomain = useSelector(selectCompanyCustomDomain);
  const domainCreating = useSelector(selectCompanyCustomDomainCreatingLoading);
  const domainDetailsLoading = useSelector(selectCompanyCustomDomainDetailsLoading);
  const domainVerifying = useSelector(selectCompanyCustomDomainVerifyLoading);
  const domainDeleting = useSelector(selectCompanyCustomDomainDeleteLoading);

  const customDomainFeature = useMemo(() => companyFeatures.find(feature => feature.name === 'custom_email_domain'), [companyFeatures]);
  const customDomainSetting = useMemo(() => companySettings.find(setting => setting.name === 'custom_email_domain'), [companySettings]);

  useEffect(() => {
    if (customDomainSetting.value && !companyDomain) {
      dispatch(getCompanyCustomDomainDetails(companyId));
    }
  }, [customDomainSetting, companyDomain]);

  const handleRegisterDomainClick = () => {
    dispatch(showModal(
      () => (
        <RegisterDomainModal
          companyId={companyId}
        />
      ),
      {
        title: 'Register Domain',
        size: 'lg'
      }
    ));
  };

  const handleVerifyDomain = () => {
    dispatch(verifyCompanyCustomDomain(companyId));
  };

  const handleDisableSetting = async () => {
    dispatch(updateCompanySetting(companyId, customDomainSetting.id, false, companyDomain.domainName));
    dispatch(createToast({ title: 'Company Setting has been disabled', status: 'Success' }));
  };

  const handleDeleteDomain = () => {
    dispatch(showModal(
      () => (
        <ConfirmActionWarningModal
          description="This will turn this setting off and remove your domain's registration from PINS.
          The SPF and DKIM records will be invalidated.  You can always restart this process, but you will need to
          re-verify new SPF and DKIM records"
        />
      ),
      {
        title: 'Remove Domain Registration?',
        size: 'md',
        buttons: {
          component: StackedButtonGroup,
          primaryButtonOptions: {
            label: 'Delete',
            isDisabled: true,
            onClick: () => {
              dispatch(deleteCompanyCustomDomain(companyId));
              dispatch(hideModal());
            }
          }
        }
      }
    ));
  };

  if (!customDomainFeature || !customDomainSetting) {
    return (
      <Box>
        <LoadingSpinner
          isInline
          minHeight="200px"
        />
      </Box>
    );
  }

  // TODO: add this back once paywall is enabled
  // if (!customDomainFeature.isEnabled) {
  //   return <CustomDomainPayWall />;
  // }

  return (
    <Box layerStyle="tabPanel">
      {customDomainSetting.value
        ? (
          <VerifyDomainHeader
            companyId={companyId}
            domainDeleting={domainDeleting}
            domainDetailsLoading={domainDetailsLoading}
            domainName={companyDomain?.domainName}
            domainStatus={companyDomain?.state}
            domainVerifying={domainVerifying}
            handleDeleteDomain={handleDeleteDomain}
            handleDisableSetting={handleDisableSetting}
            handleVerifyDomain={handleVerifyDomain}
            isEnabled={customDomainSetting.isEnabled}
          />
        )
        : (
          <RegisterNewDomainHeader
            domainCreating={domainCreating}
            handleRegisterDomainClick={handleRegisterDomainClick}
          />
        )}

      {customDomainSetting.value && (<DomainRecords companyDomain={companyDomain} />)}
    </Box>
  );
};

export default CustomDomainPanel;
